import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { conf, Schema, Resource, Attribute, Field, RelSide, RelPoint, Relationship, RelType } from 'src/config';
import { Button, Typography, Box, FormControl, InputLabel, Select, MenuItem, Grid, FormHelperText, Switch, FormControlLabel } from '@mui/material';
import _, { Dictionary } from 'lodash';
import Entity from '../components/Entity';
import AttributeProvider, { Scope as AttrScope } from '../components/Attribute';
import * as types from '../types';
import * as actions from '../actions';
import AbstractField from './controls/AbstractField';
import RelatedOne from './controls/RelatedOne';
import RelatedMany from './controls/RelatedMany';
import { isPersistent } from '../utils';
import { AccessLevel } from 'src/auth';
import GraphView from './graph/GraphView';

const EntityForm: React.FC<{
    locator: types.Locator;
    onComplete?: types.onCompleteHandler;

}> = ({
    locator,
    onComplete,
}) => {
        const dispatch = useDispatch();
        const { name, id } = locator;

        const renderAttribute = (
            attrScope: AttrScope,
            entity?: types.Entity
        ) => {
            const {
                attrName,
                attr,
                onChange,
                onLink,
                onUnLink,
                onDelete,
                value,
                errors,
                field,
                vector,
                relEntity,
                relEntities,
                stack,
                userAccess
            } = attrScope;

            if (field) {
                const field: Field = attr as Field;

                return <AbstractField
                    key={attrName}
                    field={field}
                    value={value as types.SimpleValue}
                    onChange={onChange}
                    errors={errors}
                    entity={entity}
                />
            }

            if (vector && (userAccess === AccessLevel.Manage || (isPersistent(id) && userAccess === AccessLevel.Read))) {
                const vector: RelPoint = attr as RelPoint;
                const { side: dir, rel } = vector;

                if (!rel.bidirectional && dir === RelSide.TARGET) {
                    return null;
                }

                const relatedOne: boolean = ((dir === RelSide.SOURCE) && [RelType.MANY_TO_ONE, RelType.ONE_TO_ONE].includes(rel.metaType))
                    || ((dir === RelSide.TARGET) && [RelType.ONE_TO_ONE].includes(rel.metaType));

                const relatedMany: boolean = (dir === RelSide.SOURCE) && [RelType.MANY_TO_MANY].includes(rel.metaType)
                    || (dir === RelSide.TARGET) && [RelType.MANY_TO_ONE, RelType.MANY_TO_MANY].includes(rel.metaType);

                if (relatedOne) {
                    return <RelatedOne
                        parentLocator={locator}
                        key={attrName}
                        vector={vector}
                        relEntity={relEntity}
                        onLink={onLink}
                        onUnLink={onUnLink}
                        stack={stack}
                        entity={entity}
                    />
                }

                if (relatedMany) {
                    return <RelatedMany
                        parentLocator={locator}
                        key={attrName}
                        vector={vector}
                        relEntities={relEntities}
                        onLink={onLink}
                        onUnLink={onUnLink}
                        onDelete={onDelete}
                        stack={stack}
                        entity={entity}
                    />
                }
            }
        }

        return <Entity
            locator={locator}
        >
            {({
                entity,
                attrNames,
                morphs,
                submit,
                close,
                errors,
                isAdmin,
            }) => {
                const morph = entity?.morph ? entity.morph : locator.name;

                return <Box
                    sx={{
                        position: 'relative',
                        mb: 1
                    }}
                >
                    <Grid
                        container
                        direction='row'
                        justifyContent='space-evenly'
                        spacing={1}
                        marginBottom={1}
                    >
                        <Grid padding={1}>
                            <Box sx={{ fontSize: '0.8em' }}>

                                {
                                    isAdmin && <FormControlLabel
                                        control={
                                            <Switch
                                                checked={entity?.sys === true}
                                                onChange={(e) => dispatch(actions.setShowHiddenAction(locator, (entity != null) ? (!entity.sys) : false))}
                                            />
                                        }
                                        label={'Show hidden fields'}
                                    />
                                }

                                {

                                    errors && <Grid
                                        container
                                        direction='column'
                                        justifyContent='space-around'
                                        spacing={1}
                                        marginBottom={1}
                                    >
                                        <Grid>
                                            {
                                                errors.map((err, index) => (
                                                    <FormHelperText key={index} error>
                                                        {err.message}
                                                    </FormHelperText>
                                                ))
                                            }
                                        </Grid>
                                    </Grid>

                                }

                                {
                                    morphs && <FormControl
                                        size='small'
                                        fullWidth
                                    >
                                        <InputLabel>{'Morph'}</InputLabel>
                                        <Select
                                            sx={{
                                                p: 0,
                                                m: 0
                                            }}
                                            value={morph}
                                            label={'Morph'}
                                            onChange={e => {
                                                dispatch(actions.setMorph(locator, e.target.value));
                                            }}
                                        >
                                            <MenuItem value={locator.name}>{locator.name}</MenuItem>
                                            {
                                                morphs.map((mph, i) => <MenuItem value={mph} key={i}>{mph}</MenuItem>)
                                            }
                                        </Select>
                                    </FormControl>
                                }

                                {
                                    attrNames.map(attrName => (
                                        <AttributeProvider attrName={attrName} key={attrName}>
                                            {(attrScope) => {
                                                return <Box>
                                                    {
                                                        renderAttribute(attrScope, entity)
                                                    }
                                                </Box>
                                            }}
                                        </AttributeProvider>
                                    ))
                                }

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 1,
                                }}>
                                    <Button
                                        onClick={() => {
                                            close(onComplete);
                                        }}
                                        variant='outlined'
                                        sx={{ m: 1 }}
                                    >
                                        {'Cancel'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            submit(onComplete);
                                        }}
                                        variant='contained'
                                        sx={{ m: 1 }}
                                    >
                                        {'Save'}
                                    </Button>
                                </Box>

                                {/* <pre> {JSON.stringify(entity, undefined, 2)} </pre> */}
                            </Box>
                        </Grid>

                        <Box width={600} height={600} bgcolor={theme => theme.palette.grey[100]}>
                            <GraphView graphId='graph-entities' height={600} width={600} />
                        </Box>
                    </Grid>


                </Box>
            }}
        </Entity>
    };

export default EntityForm;
